import { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import L, { type LatLngExpression, type LatLngBoundsExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPinned } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import type { EvvRecord } from '@/api/evv';

/*
 * Renders a modal with an OpenStreetMap-tiled Leaflet map showing the
 * check-in and/or check-out locations for an EVV record. Tiles are loaded
 * directly from the OSM public tile server — the HTTP request contains only
 * the zoom/x/y tile indices, not the client identity, so no PHI leaves the
 * browser. Pins are rendered client-side with divIcons (no image assets
 * shipped) to keep the bundle lean and avoid Leaflet's default-marker
 * bundler dance.
 *
 * HIPAA notes:
 *   - Coordinates are already considered PHI when linked to the session row
 *     we're viewing. The modal displays them to the admin but sends only
 *     anonymized tile requests to OSM.
 *   - Do NOT swap the tile URL for a provider that correlates coordinates
 *     with an account (Mapbox, Google Static Maps, etc.) without a BAA.
 */

const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors';

interface EvvLocationMapProps {
    record: EvvRecord;
    onClose: () => void;
}

export function EvvLocationMap({ record, onClose }: EvvLocationMapProps) {
    const inPt  = parseGps(record.gpsIn);
    const outPt = parseGps(record.gpsOut);

    const distance = useMemo(() => {
        if (!inPt || !outPt) return null;
        return haversineMeters(inPt.lat, inPt.lng, outPt.lat, outPt.lng);
    }, [inPt, outPt]);

    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="w-[min(calc(100vw-2rem),40rem)]">
                <DialogHeader>
                    <DialogTitle>Visit location</DialogTitle>
                    <DialogDescription>
                        {record.client || 'Unknown client'}{record.date ? ` — ${record.date}` : ''}
                    </DialogDescription>
                </DialogHeader>

                {!inPt && !outPt ? (
                    <p className="text-xs text-text-subtle py-4">
                        No GPS captured for this visit.
                    </p>
                ) : (
                    <>
                        <div className="relative h-72 w-full rounded-md overflow-hidden border border-border">
                            <MapContainer
                                style={{ height: '100%', width: '100%' }}
                                center={(inPt ?? outPt) as LatLngExpression}
                                zoom={15}
                                scrollWheelZoom
                                attributionControl
                            >
                                <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} maxZoom={19} />
                                <FitBounds inPt={inPt} outPt={outPt} />
                                {inPt && (
                                    <Marker position={[inPt.lat, inPt.lng]} icon={pinIcon('in')}>
                                        <Tooltip direction="top" offset={[0, -10]}>Check-in</Tooltip>
                                    </Marker>
                                )}
                                {outPt && (
                                    <Marker position={[outPt.lat, outPt.lng]} icon={pinIcon('out')}>
                                        <Tooltip direction="top" offset={[0, -10]}>Check-out</Tooltip>
                                    </Marker>
                                )}
                                {inPt && outPt && (
                                    <Polyline
                                        positions={[[inPt.lat, inPt.lng], [outPt.lat, outPt.lng]]}
                                        pathOptions={{
                                            color: record.gpsMatch ? '#15803d' : '#b45309',
                                            weight: 2,
                                            dashArray: '4 4',
                                        }}
                                    />
                                )}
                            </MapContainer>
                        </div>

                        <dl className="mt-3 grid grid-cols-2 gap-y-1 gap-x-4 text-xs">
                            <dt className="text-text-muted">Check-in GPS</dt>
                            <dd className="font-mono tabular-nums text-text">{inPt ? fmtCoord(inPt) : 'none'}</dd>
                            <dt className="text-text-muted">Check-out GPS</dt>
                            <dd className="font-mono tabular-nums text-text">{outPt ? fmtCoord(outPt) : 'none'}</dd>
                            {distance !== null && (
                                <>
                                    <dt className="text-text-muted">Distance</dt>
                                    <dd className={record.gpsMatch ? 'text-success-text' : 'text-warning-text'}>
                                        {formatMeters(distance)} {record.gpsMatch ? '(within match threshold)' : '(outside match threshold)'}
                                    </dd>
                                </>
                            )}
                        </dl>
                    </>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Small button that opens the map modal. Rendered inside the EVV report's
 * GPS cell when at least one coordinate is present; otherwise the cell just
 * shows "none" as plain text.
 */
export function EvvGpsCell({ record, onOpen }: { record: EvvRecord; onOpen: () => void }) {
    const hasGps = !!(record.gpsIn || record.gpsOut);
    if (!hasGps) return <span className="text-text-subtle">none</span>;

    const label = record.gpsMatch ? 'match' : 'off';
    const color = record.gpsMatch ? 'text-success-text' : 'text-warning-text';
    return (
        <button
            type="button"
            onClick={onOpen}
            className={`inline-flex items-center gap-1 ${color} hover:underline focus:outline-none focus:underline`}
            title="View location on map"
        >
            <MapPinned className="w-3 h-3" />
            {label}
        </button>
    );
}

// ── internals ────────────────────────────────────────────────────

function FitBounds({ inPt, outPt }: { inPt: Point | null; outPt: Point | null }) {
    const map = useMap();
    if (inPt && outPt) {
        const bounds: LatLngBoundsExpression = [[inPt.lat, inPt.lng], [outPt.lat, outPt.lng]];
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
    } else if (inPt || outPt) {
        const p = (inPt ?? outPt)!;
        map.setView([p.lat, p.lng], 16);
    }
    return null;
}

interface Point { lat: number; lng: number; }

function parseGps(raw: string | null): Point | null {
    if (!raw) return null;
    const [latStr, lngStr] = raw.split(',');
    const lat = Number(latStr);
    const lng = Number(lngStr);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
}

function fmtCoord(p: Point): string {
    return `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`;
}

function formatMeters(m: number): string {
    if (m < 1000) return `${Math.round(m)} m`;
    return `${(m / 1000).toFixed(2)} km`;
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6_371_000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
}

function pinIcon(kind: 'in' | 'out'): L.DivIcon {
    // Inline SVG pin — no image assets, inherits our color tokens via raw
    // hex fallback (Tailwind tokens aren't usable inside a Leaflet divIcon).
    const fill = kind === 'in' ? '#2563eb' : '#9333ea';
    const label = kind === 'in' ? 'IN' : 'OUT';
    const html = `
        <div style="display:flex;flex-direction:column;align-items:center;transform:translate(-50%,-100%);">
            <div style="background:${fill};color:white;font-size:9px;font-weight:700;padding:2px 5px;border-radius:4px;box-shadow:0 1px 2px rgba(0,0,0,.3);letter-spacing:.05em;">${label}</div>
            <div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:6px solid ${fill};"></div>
        </div>
    `;
    return L.divIcon({
        html,
        className: 'evv-pin',
        iconSize: [0, 0],
        iconAnchor: [0, 0],
    });
}
